import emailjs from '@emailjs/browser';
import { API_BASE_URL } from '../config';

export const EMAILJS_CONFIG = {
  SERVICE_ID: 'service_uotmdvm',
  TEMPLATE_ID: 'template_4xwvj29',
  PUBLIC_KEY: 'oQF-z1IAQjHGFA4nM',
  PRIVATE_KEY: 'NwcURi60dTNAIV4tRHxOk',
};

// Initialize EmailJS (for fallback)
try {
  emailjs.init({
    publicKey: EMAILJS_CONFIG.PUBLIC_KEY,
  });
} catch (e) {
  console.warn('EmailJS init notice:', e);
}

/**
 * Send an email using the Experts Communication SMTP backend service,
 * with graceful fallback to EmailJS.
 * @param {Object} templateParams - Form parameters
 * @returns {Promise<{success: boolean, message?: string, error?: any}>}
 */
export const sendEmail = async (templateParams) => {
  console.log('[Email Service] Dispatching form submission via SMTP...', templateParams);

  const isFormData = typeof FormData !== 'undefined' && templateParams instanceof FormData;

  // Prioritize active endpoints with a fast timeout (8s per candidate)
  // 1. Direct production API (always active on api.expertdesignhub.com)
  // 2. Configured base URL
  const backendEndpoints = [
    'https://api.expertdesignhub.com/expertscom/send-email',
    `${API_BASE_URL}/expertscom/send-email`,
    'http://localhost:3000/expertscom/send-email'
  ];
  const uniqueEndpoints = [...new Set(backendEndpoints)];

  for (const endpoint of uniqueEndpoints) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 9000);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: isFormData ? { 'Accept': 'application/json' } : {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: isFormData ? templateParams : JSON.stringify(templateParams),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const result = await response.json().catch(() => ({ success: true }));
        console.log('[SMTP Dispatch Success]', endpoint, result);
        return { success: true, ...result };
      }
    } catch (netErr) {
      // Continue to next endpoint or fallback quickly
    }
  }

  // Attempt 2: EmailJS SDK Fallback
  console.warn('[Email Service] Backend SMTP unavailable, falling back to EmailJS...');
  const fallbackParams = isFormData ? Object.fromEntries(templateParams.entries()) : templateParams;
  try {
    const response = await emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID,
      fallbackParams,
      {
        publicKey: EMAILJS_CONFIG.PUBLIC_KEY,
      }
    );
    return { success: true, status: response.status, text: response.text };
  } catch (sdkError) {
    // Attempt 3: EmailJS REST endpoint
    try {
      const restResponse = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service_id: EMAILJS_CONFIG.SERVICE_ID,
          template_id: EMAILJS_CONFIG.TEMPLATE_ID,
          user_id: EMAILJS_CONFIG.PUBLIC_KEY,
          accessToken: EMAILJS_CONFIG.PRIVATE_KEY,
          template_params: templateParams,
        }),
      });

      const restText = await restResponse.text();
      if (restResponse.ok) {
        return { success: true, status: restResponse.status, text: restText };
      } else {
        return { success: false, error: restText };
      }
    } catch (restError) {
      return { success: false, error: restError.message };
    }
  }
};

/**
 * Send an email directly from an HTML form element (supporting multipart file uploads)
 * @param {HTMLFormElement} formElement - The form DOM element
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export const sendFormEmail = async (formElement) => {
  try {
    const formData = new FormData(formElement);
    const backendEndpoints = [
      `${API_BASE_URL}/expertscom/send-email`,
      'http://localhost:3000/expertscom/send-email',
      'https://api.expertdesignhub.com/expertscom/send-email'
    ];
    const uniqueEndpoints = [...new Set(backendEndpoints)];

    for (const endpoint of uniqueEndpoints) {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          body: formData,
        });
        if (response.ok) {
          const result = await response.json().catch(() => ({ success: true }));
          return { success: true, ...result };
        }
      } catch (err) {}
    }
  } catch (err) {
    console.warn('sendFormEmail backend failed, attempting EmailJS fallback...', err);
  }

  try {
    const response = await emailjs.sendForm(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID,
      formElement,
      {
        publicKey: EMAILJS_CONFIG.PUBLIC_KEY,
      }
    );
    return { success: true, status: response.status, text: response.text };
  } catch (error) {
    console.error('EmailJS SendForm Error:', error);
    return { success: false, error };
  }
};

export default sendEmail;
